/**
 * Centered icon + headline + subtext placeholder used wherever a
 * surface has nothing to show yet — empty file lists, empty session
 * lists, fresh chats, etc.
 *
 * The visual recipe (icon size, weights, spacing, max-width) is
 * captured here so every "nothing to show" pane in the app reads as
 * the same component instead of slowly drifting apart. Pass an
 * optional ``children`` slot for a CTA (e.g. a "New session" button)
 * — it sits below the subtext with consistent breathing room.
 *
 * The root uses ``flex-1`` so when dropped into a flex column it
 * takes whatever vertical room is available and centers itself; pair
 * it with a parent that has ``min-h-full`` (or no other tall
 * children) for true visual centering.
 */

import type { LucideIcon } from 'lucide-react';
import type * as React from 'react';
import { cn } from '@/lib/utils/cn';

export interface EmptyStateProps {
  /** Lucide icon rendered above the headline (e.g. ``Sparkles``, ``MessageSquare``). */
  icon: LucideIcon;
  /** Short, sentence-case headline — the "what". */
  title: string;
  /** Optional supporting copy. Keep it under ~2 short sentences. */
  description?: React.ReactNode;
  /** Optional CTA / extra content rendered below the description. */
  children?: React.ReactNode;
  className?: string;
}

export function EmptyState({ icon: Icon, title, description, children, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center px-6 py-10 text-center', className)}>
      <Icon aria-hidden="true" className="mb-3 size-10 text-gray-300 dark:text-gray-600" />
      <p className="font-semibold text-[15px] text-gray-700 dark:text-gray-200">{title}</p>
      {description && (
        <p className="mt-1.5 max-w-sm text-[13px] text-gray-500 leading-relaxed dark:text-gray-400">{description}</p>
      )}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}
